// Everything from OpenLayers
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { Circle, Fill, Icon, Stroke, Style } from "ol/style";
import Text from "ol/style/Text";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import { fromLonLat } from "ol/proj";
import { toContext } from "ol/render";
import Polygon from "ol/geom/Polygon";

// Everything from luxon
import { DateTime } from "luxon";

import { USED_SIGN_TYPES } from "../SDOTrafficSigns";

// API parameters

const SIGN_URL = process.env.SIGN_URL;
const DATA_SOURCE = "sdo";
const TIMEOUT_MILLIS = 0;
// Change CLUSTERING to true if you want UNCLUSTERED signs.
const CLUSTERING = true;
const CLUSTER_RADIUS = 30;
const SRS = "EPSG:900913";
const VERSION = "1.3";
const PLACEMENT_TYPE = "";

// Some parameters used to render the dial and bearing used to show the direction of the sign.
const DIAL_THICKNESS = 3;
const DIAL_LENGTH = 8;
const DIAL_BORDER = 2;
const DEG_TO_RADIANS = Math.PI / 180;
const BEARING_DIAL_Y_SHIFT = 24;
const BEARING_NEEDLE_POLY = [
  [0, 0],
  [0, DIAL_LENGTH],
  [DIAL_THICKNESS, DIAL_LENGTH],
  [DIAL_THICKNESS, 0],
  [0, 0],
];

const IMAGE_SIZE = 20;
const IMAGE_SIZE_LARGER = 30;

const CLUSTERED_ZINDEX = 9;
const UNCLUSTERED_ZINDEX = 8;
const TEXT_OFFSET = 10;

// Global variables used to abort API calls because the user has changed the map.
let abortController, signal;

export class TrafficSign {
  /**
   * @param {JSON} feature The JSON object returned by the API call.
   */
  constructor(feature) {
    this.feature = feature;
    this.sign = feature.properties.sign;
    this.placement = feature.properties.placement;
    this.bearing = feature.properties.bearing; // Optional

    // Clustered traffic sign
    if (feature.geometry.coordinates) {
      this.coordinates = feature.geometry.coordinates;
      this.zIndex = CLUSTERED_ZINDEX;
      this.clusterSize = feature.properties.CLUSTER_SIZE;
    }
    // Traffic sign that stands on its own (still possible even when CLUSTERED=true)
    else if (feature.geometry.geometries[0].coordinates) {
      this.coordinates = feature.geometry.geometries[0].coordinates;
      this.zIndex = UNCLUSTERED_ZINDEX; // We want these to be layered underneath the clustered signs
    }

    this.createSignFeature();
  }

  createSignFeature() {
    this.styles = [];
    this.signFeature = new Feature({
      geometry: new Point(fromLonLat(this.coordinates)),
    });

    // SignStyle -- The TrafficSign icon.
    const signStyle = this.createSignStyle(),
      signIcon = signStyle.getImage(),
      signImage = signIcon.getImage();

    if (signImage.width) {
      signIcon.setScale(
        IMAGE_SIZE /
          TrafficSign.calculateLargest(signImage.width, signImage.height),
      );
    }

    signImage.addEventListener("load", function () {
      signIcon.setScale(
        IMAGE_SIZE /
          TrafficSign.calculateLargest(signImage.width, signImage.height),
      );
    });

    this.styles.push(signStyle);

    // ClusterStyle -- The cluster number.
    if (this.clusterSize) {
      this.styles.push(this.createClusterStyle());
    }

    // BearingStyle -- The bearing added to show the direction of the sign (hover only).
    if (this.bearing) {
      this.styles = this.styles.concat(this.createBearingStyles());
    }

    // InfoStyle -- The name of the sign (hover only).
    this.styles.push(this.createInfoStyle());

    this.signFeature.setStyle(this.styles);
  }

  createSignStyle() {
    const signStyle = new Style({
      image: new Icon({
        src: this.sign + ".png",
        // src: signIcons[this.sign] ? signIcons[this.sign] : signIcons["UNKNOWN"],
        // The initial scale is set to zero, because the size of the image is not known yet.
        scale: 0,
      }),
      zIndex: this.zIndex,
    });
    signStyle.type = "sign";

    return signStyle;
  }

  createClusterStyle() {
    const clusterStyle = new Style({
      // The cluster style should always be layered on top of the sign icon.
      zIndex: this.zIndex + 1,
      image: new Circle({
        fill: new Fill({
          color: "#526A8F",
        }),
        stroke: new Stroke({
          color: "#FFFFFF",
          width: 1.25,
        }),
        // TODO Add as constants
        radius: 8,
        displacement: [10, -10],
        scale: 1,
      }),
      text: new Text({
        text: String(this.clusterSize),
        fill: new Fill({
          color: "#FFFFFF",
        }),
        scale: 1,
        offsetX: TEXT_OFFSET,
        offsetY: TEXT_OFFSET,
      }),
    });
    clusterStyle.type = "cluster";

    return clusterStyle;
  }

  createBearingStyles() {
    const theta = this.bearing * DEG_TO_RADIANS;

    // Creation of the rectangle of the dial.
    let canvas = document.createElement("canvas");
    let vectorContext = toContext(canvas.getContext("2d"), {
      size: [DIAL_THICKNESS, DIAL_LENGTH],
      pixelRatio: 1,
    });
    vectorContext.setStyle(
      new Style({
        fill: new Fill({ color: "#006E00" }),
      }),
    );
    vectorContext.drawGeometry(new Polygon([BEARING_NEEDLE_POLY]));

    const dialStyle = new Style({
      zIndex: CLUSTERED_ZINDEX + 2,
      image: new Circle({
        radius: DIAL_LENGTH,
        fill: new Fill({
          color: "#B4FAB4",
        }),
        stroke: new Stroke({
          color: "#009600",
          width: DIAL_BORDER,
        }),
        displacement: [0, 24],
        scale: 0,
      }),
    });
    dialStyle.type = "dial";

    const needleStyle = new Style({
      zIndex: CLUSTERED_ZINDEX + 5,
      image: new Icon({
        img: canvas,
        imgSize: [DIAL_THICKNESS, DIAL_LENGTH],
        rotation: theta,
        anchorOrigin: "bottom-left",
        offsetOrigin: "bottom-left",
        anchor: [0.5, 0],
        // Because the displacement happens in the direction of the orientation, theta has to be accounted for
        // when placing the needle in the middle of the dial.
        displacement: [
          -BEARING_DIAL_Y_SHIFT * Math.sin(theta),
          BEARING_DIAL_Y_SHIFT * Math.cos(theta),
        ],
        scale: 0,
      }),
    });
    needleStyle.type = "needle";

    return [dialStyle, needleStyle];
  }

  createInfoStyle() {
    const InfoStyle = new Style({
      text: new Text({
        text: `${this.sign} (${this.placement})`,
        offsetX: 0,
        offsetY: -50,
        scale: 0,
        fill: new Fill({ color: "#FFFFFF" }),
        backgroundFill: new Fill({ color: "rgba(0, 0, 0, 0.4)" }),
        padding: 0,
      }),
    });
    InfoStyle.type = "info";

    return InfoStyle;
  }

  /**
   * @param {number} width Width in pixels
   * @param {number} height Height in pixels
   */
  static calculateLargest(width, height) {
    let largest = Math.max(width, height);
    if (Math.min(width, height) <= largest / 2) {
      largest *= 0.75;
    }
    return largest;
  }

  /**
   * @param {SelectEvent} e
   */
  static hoverSelectHandler(e) {
    if (e.selected.length !== 0) {
      if (e.selected[0] instanceof Feature) {
        TrafficSign.changeFeatureScale(e.selected[0], true);
      }
    }

    if (e.deselected.length !== 0) {
      if (e.deselected[0] instanceof Feature) {
        TrafficSign.changeFeatureScale(e.deselected[0], false);
      }
    }

    // Force the layer to rerender.
    TrafficSign.sdoLayer.getSource().changed();
  }

  /**
   * @param {Feature} feature
   * @param {boolean} hover
   */
  static changeFeatureScale(feature, hover) {
    var featureStyle = feature.getStyle();
    let icon, image;

    // This should only apply to WKT objects, as the SDO features should have styles.
    if (featureStyle == null) {
      return;
    }

    // Forcefully change featureStyle to an array if the feature only has one style.
    // This way, the following map function always succeeds.
    if (!Array.isArray(featureStyle)) {
      featureStyle = [featureStyle];
    }

    // This is a useless check but allows the map function to understand the type of 'style'.
    if (Array.isArray(featureStyle)) {
      featureStyle.map(function (style) {
        switch (style.type) {
          case "sign":
            icon = style.getImage();
            image = icon.getImage();
            icon.setScale(
              (hover ? IMAGE_SIZE_LARGER : IMAGE_SIZE) /
                TrafficSign.calculateLargest(image.width, image.height),
            );
            break;
          case "cluster":
            icon = style.getImage();
            icon.setScale(hover ? 1.3 : 1);
            style.getText().setScale(hover ? 1.3 : 1);
            style
              .getText()
              .setOffsetX(
                (hover ? 1.3 : Math.pow(1.3, -1)) *
                  style.getText().getOffsetX(),
              );
            style
              .getText()
              .setOffsetY(
                (hover ? 1.3 : Math.pow(1.3, -1)) *
                  style.getText().getOffsetY(),
              );
            break;
          case "dial":
          case "needle":
            icon = style.getImage();
            icon.setScale(hover ? 1 : 0);
            break;
          case "info":
            style.getText().setScale(hover ? 1 : 0);
            style.getText().setPadding(hover ? [8, 8, 8, 8] : null);
            break;
        }

        if (typeof style.getZIndex === "function") {
          style.setZIndex(
            hover ? style.getZIndex() + 3 : style.getZIndex() - 3,
          );
        }
      });
    }
  }
}

/**
 * @param {string} startDate
 * @param {string} endDate
 */
function convertDateAndTime(startDate, endDate) {
  // https://confluence.tomtomgroup.com/display/FCD/GeoJSON+Observations for date and time formats.
  const TIME =
    DateTime.fromISO(startDate).toISODate() +
    "/" +
    DateTime.fromISO(endDate).toISODate();

  return { TIME: TIME };
}

/**
 * @param {SDOControl} sdoControl
 */
function calculateArea(sdoControl) {
  const bbox = sdoControl.get("bbox");
  return Math.abs((bbox[0] - bbox[2]) * Math.abs(bbox[1] - bbox[3]));
}

/**
 * @param {DateInputControl} dateInputControl
 * @param {SDOControl} sdoControl
 * @param {AzureLoginControl} azureLoginControl
 * @param {VectorSource} vectorSource
 * @param {VectorLayer} vectorLayer
 */
function propertyChangeHandler(
  dateInputControl,
  sdoControl,
  azureLoginControl,
  vectorSource,
  vectorLayer,
) {
  if (!azureLoginControl.get("idToken")) {
    vectorLayer.setVisible(false);
    sdoControl.set("idTokenSet", false);
  } else {
    sdoControl.set("idTokenSet", true);
    if (sdoControl.get("sdoToggle") == true) {
      vectorLayer.setVisible(true);
      updateTrafficSigns(
        dateInputControl,
        sdoControl,
        azureLoginControl,
        vectorSource,
        vectorLayer,
      );
    } else {
      // When sdoToggle is not set, the checkbox is not checked, and so the layer is made invisible.
      vectorLayer.setVisible(false);
    }
  }
}

/**
 * @param {DateInputControl} dateInputControl
 * @param {SDOControl} sdoControl
 * @param {AzureLoginControl} azureLoginControl
 * @param {VectorSource} vectorSource
 * @param {VectorLayer} vectorLayer
 */
function updateTrafficSigns(
  dateInputControl,
  sdoControl,
  azureLoginControl,
  vectorSource,
  vectorLayer,
) {
  if (abortController) {
    abortController.abort();
  }

  // 'signal' is a global variable used to abort fetch requests when a new request is already made.
  // The API is rather slow, so it would be a waste of recourses to let the fetch continue to work
  // after a new move on the map was made.
  abortController = new AbortController();
  signal = abortController.signal;

  // The area for the traffic signs can be 12 km2 maximum.
  if (calculateArea(sdoControl) <= 12 * Math.pow(10, 6)) {
    if (vectorLayer) {
      vectorLayer.setVisible(true);
    }
    sdoControl.set("tooLarge", false);
    sdoControl.set("loading", true);

    const dateAndTimeParams = convertDateAndTime(
      dateInputControl.get("startDate"),
      dateInputControl.get("endDate"),
    );
    const searchParams = new URLSearchParams();

    searchParams.set("DATA_SOURCE", DATA_SOURCE);
    searchParams.set("TIMEOUT_MILLIS", TIMEOUT_MILLIS);
    searchParams.set("SIGN_TYPE", USED_SIGN_TYPES);
    searchParams.set("CLUSTERING", CLUSTERING);
    searchParams.set("CLUSTER_RADIUS", CLUSTER_RADIUS);
    searchParams.set("TIME", dateAndTimeParams.TIME);
    searchParams.set("bbox", sdoControl.get("bbox"));
    searchParams.set("SRS", SRS);
    searchParams.set("VERSION", VERSION);
    searchParams.set("PLACEMENT_TYPE", PLACEMENT_TYPE);

    const headers = {
      Authorization: "Bearer " + azureLoginControl.get("idToken"),
    };

    fetch(SIGN_URL + "?" + searchParams.toString(), { signal, headers })
      .then(function (response) {
        return response.json();
      })
      .then(function (jsonResponse) {
        // All TrafficSigns are removed from the map upon a new JSON response from the API.
        vectorSource.clear();

        jsonResponse.features.map(function (feature) {
          const trafficSign = new TrafficSign(feature, vectorSource);
          vectorSource.addFeature(trafficSign.signFeature);
        });

        sdoControl.set("loading", false);
      })
      .catch(function (error) {
        if (error.name === "AbortError") {
          // The request has been canceled due to a new move event.
          // This is an intended error, so this is not logged to the console.
        } else if (error.name === "SyntaxError") {
          // No data is available.
          console.error("No SDO data is available for this date range.");
          sdoControl.set("loading", false);
        } else {
          console.error(error);
        }
      });
  } else {
    sdoControl.set("loading", false);
    sdoControl.set("tooLarge", true);
    if (vectorLayer) {
      vectorLayer.setVisible(false);
    }
  }
}

/**
 * @param {DateInputControl} dateInputControl
 * @param {SDOControl} sdoControl
 * @param {AzureLoginControl} azureLoginControl
 * @returns
 */
export function getSDOLayer(dateInputControl, sdoControl, azureLoginControl) {
  const vectorSource = new VectorSource({
    features: [],
  });

  const vectorLayer = new VectorLayer({
    source: vectorSource,
    zIndex: 10,
  });

  sdoControl.on("propertychange", function (e) {
    if (e.key !== "loading" && e.key !== "tooLarge") {
      propertyChangeHandler(
        dateInputControl,
        sdoControl,
        azureLoginControl,
        vectorSource,
        vectorLayer,
      );
    }
  });

  dateInputControl.on("propertychange", function () {
    propertyChangeHandler(
      dateInputControl,
      sdoControl,
      azureLoginControl,
      vectorSource,
      vectorLayer,
    );
  });

  azureLoginControl.on("propertychange", function () {
    propertyChangeHandler(
      dateInputControl,
      sdoControl,
      azureLoginControl,
      vectorSource,
      vectorLayer,
    );
  });

  return vectorLayer;
}
